<template>
  <div class="container">
    <TopBar @close="handleClose" @manualInput="openManualInput" />
    
    <div v-if="!manualMode" class="message-text"><span>Scannez le Qr code du varibox</span></div>

    <div v-else class="manual-code">
      <input
  v-model="manualCode"
  ref="inputField"
  type="number"
  placeholder="Entrez le code"
  @keyup.enter="validateManualCode"
/>
    </div>

    <ScannerComponent/>
    <FlashlightComponent/>
    <video ref="video" class="video-bg" playsinline autoplay muted></video>
  </div>
</template>

<script setup>
import { onMounted, ref, onUnmounted, nextTick } from 'vue'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { useRouter } from 'vue-router'
import { useOverlayStore } from '@/stores/overlay'
import { useScannedQrStore } from '@/stores/qrCode'

import ScannerComponent from './qrscanner_component.vue'
import FlashlightComponent from './flashlight_component.vue'
import TopBar from './top_bar_component.vue'

const router = useRouter()
const overlayStore = useOverlayStore()
const scannedQrStore = useScannedQrStore()

const video = ref(null)
let codeReader = null
let stream = null
let decodingActive = false

const manualMode = ref(false)
const manualCode = ref('')
const inputField = ref(null)

const handleClose = () => {
  decodingActive = false
  overlayStore.toggleQrScan(false)
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
  }
}

// Activation du mode écriture
const openManualInput = async () => {
  decodingActive = false

  // Stopper la caméra proprement avant
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
    stream = null
  }

  manualMode.value = true

  // Attendre que l'input apparaisse
  await nextTick()

  inputField.value?.focus()
}

// Validation manuelle du code
const validateManualCode = async () => {
  if (/^\d+$/.test(manualCode.value) && parseInt(manualCode.value) <= 999999) {
    scannedQrStore.setQrCode(manualCode.value)

    if (navigator.vibrate) navigator.vibrate(200)

    manualMode.value = false
    handleClose()

    await new Promise(resolve => setTimeout(resolve, 300))
    router.push('/varibox')
  } else {
    alert('Veuillez entrer un code valide')
  }
}


onMounted(async () => {
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      console.warn('Pas de caméra disponible')
      return
    }

    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: 'environment' }, advanced: [{ focusMode: 'continuous' }] }
    })

    if (video.value) {
      video.value.srcObject = stream
      await video.value.play()

      codeReader = new BrowserMultiFormatReader()
      decodingActive = true

      startScanning()
    }
  } catch (error) {
    console.warn('⚠️ Erreur caméra :', error)
  }
})

const startScanning = async () => {
  try {
    while (decodingActive) {
      const result = await codeReader.decodeOnceFromVideoElement(video.value)

      if (result) {
        const text = result.getText()
        console.log('✅ QR Code détecté :', text)

        if (/^\d+$/.test(text) && parseInt(text) <= 999999) {
          if (navigator.vibrate) navigator.vibrate(200)

          scannedQrStore.setQrCode(text)

          decodingActive = false
          handleClose()

          await new Promise(resolve => setTimeout(resolve, 300))
          router.push('/varibox')
        } else {
          console.warn('❌ QR Code non valide.')
        }
      }
    }
  } catch (error) {
    console.warn('Erreur pendant le scan :', error)
  }
}

onUnmounted(() => {
  decodingActive = false
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
  }
  if (codeReader) {
    codeReader.reset()
  }
})
</script>

<style scoped>
.container {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  width: 100%;
  height: 100%;
  background-color: black;
  z-index: 999;
  position: relative;
}

.video-bg {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  z-index: 1000;
}

.message-text {
  pointer-events: none;
  position: absolute;
  color: white;
  top: 15vh;
  left: 50%;
  transform: translate(-50%, 0);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: clamp(1rem, 3vw, 1.3rem);
  z-index: 1001;
}

.manual-code {
  position: absolute;
  top: 15vh;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 1002;
}

.manual-code input{
  font-size: clamp(0.7rem, 3vw, 1rem);
  color: white;
  padding: 5px;
  border: none;
  border-radius: 50px;
  background: rgba(0, 0, 0, 0.4); /* noir transparent */
  backdrop-filter: blur(10px); /* flou derrière */
  -webkit-backdrop-filter: blur(10px);
  width: 35vw;
  height: 5vh;
  text-align: center;
  text-decoration: none;
}
</style>